import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Search, Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/use-toast";
import { ProductCard } from "@/components/ProductCard";
import { PersonalizacionCard } from "@/components/PersonalizacionCard";
import {
  CartNotification,
  useCartNotification,
} from "@/components/CartNotification";
import { useAuth } from "@/contexts/AuthContext";
import productService, {
  Product,
  ProductFilters,
} from "@/services/productService";
import { gtmEvent, mapItemToGA4 } from "@/lib/gtm";

const Products = () => {
  const { toast } = useToast();
  const { notification, showNotification, hideNotification } =
    useCartNotification();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ProductFilters>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const {
    data: products,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["products", filters],
    queryFn: () => productService.getProducts(filters),
  });

  // Obtener tags desde la API
  const { data: tags } = useQuery({
    queryKey: ["tags"],
    queryFn: () => productService.getTags(),
    staleTime: 10 * 60 * 1000, // 10 minutos
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery) {
        setFilters((prev) => ({ ...prev, search: searchQuery }));
      } else {
        setFilters((prev) => {
          const { search, ...rest } = prev;
          return rest;
        });
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleFilterChange = (
    key: keyof ProductFilters,
    value: string | number | string[] | undefined
  ) => {
    setFilters((prev) => {
      if (value === undefined || value === "") {
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: value };
    });
  };

  const clearFilters = () => {
    setFilters({});
    setSearchQuery("");
  };

  // GTM: view_item_list cuando los productos cargan
  useEffect(() => {
    if (!products?.length) return;
    gtmEvent("view_item_list", {
      ecommerce: {
        currency: "USD",
        items: products.slice(0, 12).map((p) =>
          mapItemToGA4({ id: p._id, name: p.title, price: p.price, type: p.type })
        ),
      },
    });
  }, [products]);

  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: "No se pudieron cargar los productos",
        variant: "destructive",
      });
    }
  }, [error, toast]);

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header y Filtros */}
      <div className="mb-8">
        <h1 className="text-3xl font-playfair font-bold text-brown mb-4">
          Nuestros Productos
        </h1>

        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar productos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 touch-manipulation min-h-[44px]"
            aria-expanded={showFilters}
            aria-label="Mostrar u ocultar filtros"
          >
            <Filter className="h-4 w-4" />
            <span className="hidden sm:inline">Filtros</span>
            <span className="sm:hidden">{showFilters ? 'Ocultar' : 'Filtros'}</span>
          </Button>
        </div>

        {/* Panel de Filtros */}
        {showFilters && (
          <div className="mt-4 p-4 border rounded-lg bg-white shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium">Filtros</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4 mr-2" />
                Limpiar
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Tipo</label>
                <select
                  className="w-full p-2 border rounded-md min-h-[44px] text-base"
                  value={filters.type || ""}
                  onChange={(e) =>
                    handleFilterChange("type", e.target.value || undefined)
                  }
                >
                  <option value="">Todos</option>
                  <option value="físico">Físico</option>
                  <option value="digital">Digital</option>
                  <option value="taller">🎨 Talleres</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Precio Mínimo
                </label>
                <Input
                  type="number"
                  value={filters.minPrice || ""}
                  onChange={(e) =>
                    handleFilterChange(
                      "minPrice",
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  placeholder="0"
                  className="min-h-[44px] text-base"
                  inputMode="numeric"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Precio Máximo
                </label>
                <Input
                  type="number"
                  value={filters.maxPrice || ""}
                  onChange={(e) =>
                    handleFilterChange(
                      "maxPrice",
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  placeholder="1000"
                  className="min-h-[44px] text-base"
                  inputMode="numeric"
                />
              </div>
            </div>

            {tags && tags.length > 0 && (
              <div className="mt-4">
                <label className="text-sm font-medium mb-2 block">
                  Etiquetas
                </label>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <Badge
                      key={tag}
                      variant={
                        filters.tags?.includes(tag) ? "default" : "outline"
                      }
                      className="cursor-pointer"
                      onClick={() => {
                        const currentTags = filters.tags || [];
                        const newTags = currentTags.includes(tag)
                          ? currentTags.filter((t) => t !== tag)
                          : [...currentTags, tag];
                        handleFilterChange(
                          "tags",
                          newTags.length ? newTags : undefined
                        );
                      }}
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* CEO Planner Personalizado - Destacado */}
      <div className="mb-12 animate-fade-in">
        <PersonalizacionCard />
      </div>

      {/* Grid de Productos */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
        {isLoading ? (
          // Skeleton loaders
          Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="space-y-3">
              <Skeleton className="h-40 sm:h-64 w-full rounded-2xl" />
              <Skeleton className="h-6 w-3/4 mx-auto" />
              <Skeleton className="h-4 w-1/2 mx-auto" />
            </div>
          ))
        ) : products?.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <p className="text-muted-foreground">No se encontraron productos</p>
          </div>
        ) : (
          products?.map((product: Product) => (
            <ProductCard
              key={product._id}
              id={product._id}
              image={product.image}
              title={product.title}
              price={product.price}
              type={product.type === "físico" ? "physical" : product.type === "taller" ? "taller" : "digital"}
              tallerProducto={product.type === "taller" ? product : undefined}
              stock={product.stock}
              category={product.category}
              active={product.isActive}
              permiteNombrePersonalizado={product.permiteNombrePersonalizado}
              esPersonalizable={product.esPersonalizable}
              precioPersonalizado={product.precioPersonalizado}
              slug={product.slug}
              orientacion={product.orientacion}
              opcionesTamano={
                product.esPersonalizable
                  ? product.configuracionPersonalizable?.personalizacion?.opcionesTamano
                  : product.opcionesTamano
              }
              opcionesTamanoEstandar={
                product.esPersonalizable
                  ? product.opcionesTamano
                  : undefined
              }
            />
          ))
        )}
      </div>

      {/* Cart Notification */}
      <CartNotification
        show={notification.show}
        productName={notification.productName}
        onClose={hideNotification}
      />
    </div>
  );
};

export default Products;
